import React, { useState, useEffect } from 'react'
import { apiService } from '../services/apiService'
import toast from 'react-hot-toast'
import {
  CogIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'

const IntegrationsPage = () => {
  const [integrations, setIntegrations] = useState([])
  const [selectedIntegration, setSelectedIntegration] = useState(null)
  const [features, setFeatures] = useState([])
  const [loading, setLoading] = useState(true)
  const [testing, setTesting] = useState(false)

  useEffect(() => {
    loadIntegrations()
  }, [])

  const loadIntegrations = async () => {
    try {
      setLoading(true)
      const response = await apiService.getIntegrations()
      setIntegrations(response.integrations)
    } catch (error) {
      console.error('Erro ao carregar integrações:', error)
      toast.error('Erro ao carregar integrações')
    } finally {
      setLoading(false)
    }
  }

  const loadFeatures = async (integrationName) => {
    try {
      const response = await apiService.getIntegrationFeatures(integrationName)
      setFeatures(response.features)
    } catch (error) {
      console.error('Erro ao carregar funcionalidades:', error)
      toast.error('Erro ao carregar funcionalidades')
    }
  }

  const handleSelectIntegration = async (integration) => {
    setSelectedIntegration(integration)
    await loadFeatures(integration.integration_name)
  }

  const handleUpdateIntegration = async (data) => {
    try {
      // Enviar apenas os campos aceitos pelo backend
      const validData = {
        enabled: data.enabled,
        sandbox_mode: data.sandbox_mode,
        api_key: data.api_key,
        webhook_secret: data.webhook_secret,
        phone_number_id: data.phone_number_id,
        business_account_id: data.business_account_id,
        config_data: data.config_data || data.settings // Suporta ambos os nomes
      }

      // Remover campos undefined/null
      Object.keys(validData).forEach(key => {
        if (validData[key] === undefined) {
          delete validData[key]
        }
      })

      console.log('📤 Atualizando integração:', selectedIntegration.integration_name, validData)

      await apiService.updateIntegration(selectedIntegration.integration_name, validData)
      toast.success('Configuração atualizada com sucesso!')
      await loadIntegrations()

      // Atualizar integração selecionada
      const updated = integrations.find(i => i.integration_name === selectedIntegration.integration_name)
      setSelectedIntegration({ ...selectedIntegration, ...data })
    } catch (error) {
      console.error('Erro ao atualizar:', error)
      toast.error(error.response?.data?.details || 'Erro ao atualizar configuração')
    }
  }

  const handleToggleFeature = async (featureKey, enabled) => {
    try {
      await apiService.updateIntegrationFeature(
        selectedIntegration.integration_name,
        featureKey,
        { enabled }
      )
      toast.success('Funcionalidade atualizada!')
      await loadFeatures(selectedIntegration.integration_name)
    } catch (error) {
      console.error('Erro ao atualizar funcionalidade:', error)
      toast.error('Erro ao atualizar funcionalidade')
    }
  }

  const handleTestConnection = async () => {
    try {
      setTesting(true)
      const response = await apiService.testIntegration(selectedIntegration.integration_name)
      
      if (response.success) {
        toast.success(response.message)
      } else {
        toast.error(response.message || 'Falha no teste de conexão')
      }
    } catch (error) {
      console.error('Erro ao testar conexão:', error)
      toast.error('Erro ao testar conexão')
    } finally {
      setTesting(false)
    }
  }

  const getIntegrationIcon = (name) => {
    const icons = {
      asaas: '💰',
      docusign: '📝',
      getrak: '📍',
      whatsapp: '📱',
      openai: '🤖',
      anthropic: '🧠'
    }
    return icons[name] || '🔌'
  }

  const getIntegrationName = (name) => {
    const names = {
      asaas: 'Asaas',
      docusign: 'DocuSign',
      getrak: 'GETRAK',
      whatsapp: 'WhatsApp Business',
      openai: 'OpenAI GPT-4',
      anthropic: 'Anthropic Claude'
    }
    return names[name] || name
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Carregando integrações...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Integrações Externas
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Configure e gerencie integrações com sistemas externos
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista de Integrações */}
        <div className="space-y-4">
          {integrations.map((integration) => (
            <div
              key={integration.id}
              onClick={() => handleSelectIntegration(integration)}
              className={`card cursor-pointer transition-all ${
                selectedIntegration?.id === integration.id
                  ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'hover:shadow-lg'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="text-3xl">
                    {getIntegrationIcon(integration.integration_name)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {getIntegrationName(integration.integration_name)}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {integration.sandbox_mode ? 'Sandbox' : 'Produção'}
                    </p>
                  </div>
                </div>
                {integration.enabled ? (
                  <CheckCircleIcon className="w-6 h-6 text-green-500" />
                ) : (
                  <XCircleIcon className="w-6 h-6 text-gray-400" />
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Configurações da Integração Selecionada */}
        {selectedIntegration && (
          <div className="lg:col-span-2 space-y-6">
            {/* Configurações Gerais */}
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Configurações - {getIntegrationName(selectedIntegration.integration_name)}
              </h2>
              
              <div className="space-y-4">
                {/* Toggle Ativo */}
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Integração Ativa
                    </label>
                    <p className="text-xs text-gray-500">
                      Habilitar ou desabilitar esta integração
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedIntegration.enabled}
                      onChange={(e) => handleUpdateIntegration({ 
                        ...selectedIntegration, 
                        enabled: e.target.checked 
                      })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                {/* Toggle Sandbox */}
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Modo Sandbox
                    </label>
                    <p className="text-xs text-gray-500">
                      Usar ambiente de testes
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedIntegration.sandbox_mode}
                      onChange={(e) => handleUpdateIntegration({ 
                        ...selectedIntegration, 
                        sandbox_mode: e.target.checked 
                      })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                {/* API Key */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {selectedIntegration.integration_name === 'whatsapp' ? 'Access Token' : 'API Key / Token'}
                  </label>
                  <input
                    type="password"
                    value={selectedIntegration.api_key || ''}
                    onChange={(e) => setSelectedIntegration({
                      ...selectedIntegration,
                      api_key: e.target.value
                    })}
                    onBlur={() => handleUpdateIntegration(selectedIntegration)}
                    placeholder={selectedIntegration.integration_name === 'whatsapp' ? 'Cole seu Access Token' : 'Cole sua API Key aqui'}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  {selectedIntegration.integration_name === 'whatsapp' && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      🔑 Token permanente obtido no Facebook Business Manager
                    </p>
                  )}
                </div>

                {/* WhatsApp Phone Number ID */}
                {selectedIntegration.integration_name === 'whatsapp' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Phone Number ID
                      </label>
                      <input
                        type="text"
                        value={selectedIntegration.phone_number_id || ''}
                        onChange={(e) => setSelectedIntegration({
                          ...selectedIntegration,
                          phone_number_id: e.target.value
                        })}
                        onBlur={() => handleUpdateIntegration(selectedIntegration)}
                        placeholder="Cole seu Phone Number ID"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        📱 WhatsApp Business API Oficial (Meta/Facebook)
                        <br />
                        Configure em: <a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">business.facebook.com</a>
                        <br />
                        💰 <strong>1.000 conversas GRÁTIS/mês</strong> + Selo verde verificado ✓
                      </p>
                    </div>

                    {/* WhatsApp Business Account ID */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Identificação da conta do WhatsApp Business
                      </label>
                      <input
                        type="text"
                        value={selectedIntegration.business_account_id || ''}
                        onChange={(e) => setSelectedIntegration({
                          ...selectedIntegration,
                          business_account_id: e.target.value
                        })}
                        onBlur={() => handleUpdateIntegration(selectedIntegration)}
                        placeholder="Cole seu Business Account ID"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        🏢 ID da conta comercial do WhatsApp no Meta Business Manager
                      </p>
                    </div>
                  </>
                )}

                {/* Webhook Secret (Clicksign) */}
                {selectedIntegration.integration_name === 'clicksign' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Webhook Secret (HMAC SHA256)
                      </label>
                      <input
                        type="password"
                        value={selectedIntegration.webhook_secret || ''}
                        onChange={(e) => setSelectedIntegration({
                          ...selectedIntegration,
                          webhook_secret: e.target.value
                        })}
                        onBlur={() => handleUpdateIntegration(selectedIntegration)}
                        placeholder="Cole o HMAC Secret aqui"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        🔒 Secret fornecido pelo Clicksign ao configurar webhook
                        <br />
                        URL do Webhook: <span className="font-mono text-blue-600 dark:text-blue-400">https://viploclocadora.com.br/api/clicksign/webhooks</span>
                      </p>
                    </div>

                    {/* Validações de Segurança (Biometria) */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                        🔐 Validações de Segurança para Assinaturas
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                        Configure quais validações os signatários precisarão passar ao assinar documentos.
                        Estas configurações serão aplicadas a TODOS os contratos enviados para o Clicksign.
                      </p>

                      <div className="space-y-3 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                        {/* SELEÇÃO DO TIPO DE BIOMETRIA */}
                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 p-4 rounded-lg mb-4">
                          <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                            🔐 Tipo de Validação Biométrica
                          </h5>
                          <div className="space-y-2">
                            {/* Biometria Facial Padrão */}
                            <label className="flex items-start gap-3 cursor-pointer p-3 rounded border-2 transition-all hover:bg-blue-100 dark:hover:bg-blue-900/40"
                              style={{
                                borderColor: selectedIntegration.config_data?.facial_biometrics_enabled
                                  ? 'rgb(59, 130, 246)'
                                  : 'transparent',
                                backgroundColor: selectedIntegration.config_data?.facial_biometrics_enabled
                                  ? 'rgb(219, 234, 254)'
                                  : 'transparent'
                              }}
                            >
                              <input
                                type="radio"
                                name="biometric-type"
                                checked={selectedIntegration.config_data?.facial_biometrics_enabled || false}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    const updated = {
                                      ...selectedIntegration,
                                      config_data: {
                                        ...selectedIntegration.config_data,
                                        facial_biometrics_enabled: true,
                                        biometric_serpro_enabled: false,
                                        liveness_enabled: false,
                                        official_document_enabled: false,
                                        selfie_enabled: false
                                      }
                                    };
                                    setSelectedIntegration(updated);
                                    handleUpdateIntegration(updated);
                                  }
                                }}
                                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                              />
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  ⭐ Biometria Facial Padrão - R$ 1,50 por assinatura
                                </div>
                                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                  Compara o rosto do signatário com a foto do documento oficial. Validação rápida e econômica.
                                  Inclui detecção de vida (liveness) para evitar fotos falsas.
                                </div>
                              </div>
                            </label>

                            {/* Biometria SERPRO */}
                            <label className="flex items-start gap-3 cursor-pointer p-3 rounded border-2 transition-all hover:bg-green-100 dark:hover:bg-green-900/40"
                              style={{
                                borderColor: selectedIntegration.config_data?.biometric_serpro_enabled
                                  ? 'rgb(34, 197, 94)'
                                  : 'transparent',
                                backgroundColor: selectedIntegration.config_data?.biometric_serpro_enabled
                                  ? 'rgb(220, 252, 231)'
                                  : 'transparent'
                              }}
                            >
                              <input
                                type="radio"
                                name="biometric-type"
                                checked={selectedIntegration.config_data?.biometric_serpro_enabled || false}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    const updated = {
                                      ...selectedIntegration,
                                      config_data: {
                                        ...selectedIntegration.config_data,
                                        biometric_serpro_enabled: true,
                                        facial_biometrics_enabled: false,
                                        liveness_enabled: false,
                                        official_document_enabled: false,
                                        selfie_enabled: false
                                      }
                                    };
                                    setSelectedIntegration(updated);
                                    handleUpdateIntegration(updated);
                                  }
                                }}
                                className="mt-1 w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                              />
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  🏛️ Biometria SERPRO - Governo Federal - R$ 4,50 por assinatura
                                </div>
                                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                  Validação biométrica através da base de dados oficial do Governo Federal (SERPRO).
                                  Máxima segurança e conformidade legal. <strong>Requer CPF válido.</strong>
                                </div>
                              </div>
                            </label>

                            {/* Opção: Nenhuma Biometria */}
                            <label className="flex items-start gap-3 cursor-pointer p-3 rounded border-2 transition-all hover:bg-gray-100 dark:hover:bg-gray-700"
                              style={{
                                borderColor: (!selectedIntegration.config_data?.facial_biometrics_enabled && !selectedIntegration.config_data?.biometric_serpro_enabled)
                                  ? 'rgb(156, 163, 175)'
                                  : 'transparent',
                                backgroundColor: (!selectedIntegration.config_data?.facial_biometrics_enabled && !selectedIntegration.config_data?.biometric_serpro_enabled)
                                  ? 'rgb(243, 244, 246)'
                                  : 'transparent'
                              }}
                            >
                              <input
                                type="radio"
                                name="biometric-type"
                                checked={!selectedIntegration.config_data?.facial_biometrics_enabled && !selectedIntegration.config_data?.biometric_serpro_enabled}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    const updated = {
                                      ...selectedIntegration,
                                      config_data: {
                                        ...selectedIntegration.config_data,
                                        facial_biometrics_enabled: false,
                                        biometric_serpro_enabled: false
                                      }
                                    };
                                    setSelectedIntegration(updated);
                                    handleUpdateIntegration(updated);
                                  }
                                }}
                                className="mt-1 w-4 h-4 text-gray-600 border-gray-300 focus:ring-gray-500"
                              />
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  ⚪ Sem Validação Biométrica
                                </div>
                                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                  Apenas validação por email. Você ainda pode habilitar outras validações abaixo (selfie, documento oficial, etc).
                                </div>
                              </div>
                            </label>
                          </div>
                        </div>

                        {/* VALIDAÇÕES ADICIONAIS (Opcionais) */}
                        <h5 className="text-sm font-semibold text-gray-900 dark:text-white mt-6 mb-2">
                          ⚙️ Validações Adicionais (Opcionais)
                        </h5>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                          Estas validações são independentes e podem ser combinadas com ou sem biometria.
                        </p>

                        {/* Biometria Facial - MANTIDO PARA COMPATIBILIDADE (HIDDEN) */}
                        <div className="hidden flex items-start gap-3">
                          <input
                            type="checkbox"
                            id="facial-biometrics"
                            checked={selectedIntegration.config_data?.facial_biometrics_enabled || false}
                            onChange={(e) => {
                              const updated = {
                                ...selectedIntegration,
                                config_data: {
                                  ...selectedIntegration.config_data,
                                  facial_biometrics_enabled: e.target.checked,
                                  // Se habilitar biometria facial: ativar liveness, desabilitar documento oficial e selfie
                                  liveness_enabled: e.target.checked ? true : selectedIntegration.config_data?.liveness_enabled,
                                  official_document_enabled: e.target.checked ? false : selectedIntegration.config_data?.official_document_enabled,
                                  selfie_enabled: e.target.checked ? false : selectedIntegration.config_data?.selfie_enabled
                                }
                              };
                              setSelectedIntegration(updated);
                              handleUpdateIntegration(updated);
                            }}
                            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <label htmlFor="facial-biometrics" className="flex-1 cursor-pointer">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              ⭐ Biometria Facial (Recomendado)
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                              Compara o rosto com a foto do documento (já inclui selfie + validação do documento).
                              Ativa Liveness. INCOMPATÍVEL com "Selfie" e "Documento Oficial".
                            </div>
                          </label>
                        </div>

                        {/* Liveness (Prova de Vida) */}
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            id="liveness"
                            checked={selectedIntegration.config_data?.liveness_enabled || false}
                            onChange={(e) => {
                              const updated = {
                                ...selectedIntegration,
                                config_data: {
                                  ...selectedIntegration.config_data,
                                  liveness_enabled: e.target.checked
                                }
                              };
                              setSelectedIntegration(updated);
                              handleUpdateIntegration(updated);
                            }}
                            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <label htmlFor="liveness" className="flex-1 cursor-pointer">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              👤 Prova de Vida (Liveness)
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                              Detecta que é uma pessoa real, não uma foto impressa ou vídeo gravado.
                            </div>
                          </label>
                        </div>

                        {/* Selfie */}
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            id="selfie"
                            checked={selectedIntegration.config_data?.selfie_enabled || false}
                            onChange={(e) => {
                              const updated = {
                                ...selectedIntegration,
                                config_data: {
                                  ...selectedIntegration.config_data,
                                  selfie_enabled: e.target.checked,
                                  // Se habilitar selfie, DESABILITAR biometria facial
                                  facial_biometrics_enabled: e.target.checked ? false : selectedIntegration.config_data?.facial_biometrics_enabled
                                }
                              };
                              setSelectedIntegration(updated);
                              handleUpdateIntegration(updated);
                            }}
                            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <label htmlFor="selfie" className="flex-1 cursor-pointer">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              📸 Selfie
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                              Signatário tira selfie ao assinar (SEM comparação com documento).
                              INCOMPATÍVEL com "Biometria Facial".
                            </div>
                          </label>
                        </div>

                        {/* Foto do Documento */}
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            id="official-document"
                            checked={selectedIntegration.config_data?.official_document_enabled || false}
                            onChange={(e) => {
                              const updated = {
                                ...selectedIntegration,
                                config_data: {
                                  ...selectedIntegration.config_data,
                                  official_document_enabled: e.target.checked,
                                  // Se habilitar documento oficial, DESABILITAR biometria facial
                                  facial_biometrics_enabled: e.target.checked ? false : selectedIntegration.config_data?.facial_biometrics_enabled
                                }
                              };
                              setSelectedIntegration(updated);
                              handleUpdateIntegration(updated);
                            }}
                            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <label htmlFor="official-document" className="flex-1 cursor-pointer">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              🪪 Foto do Documento Oficial
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                              Solicita foto do RG ou CNH para validação (SEM comparação biométrica).
                              INCOMPATÍVEL com "Biometria Facial".
                            </div>
                          </label>
                        </div>

                        {/* Assinatura Manuscrita */}
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            id="handwritten"
                            checked={selectedIntegration.config_data?.handwritten_enabled || false}
                            onChange={(e) => {
                              const updated = {
                                ...selectedIntegration,
                                config_data: {
                                  ...selectedIntegration.config_data,
                                  handwritten_enabled: e.target.checked
                                }
                              };
                              setSelectedIntegration(updated);
                              handleUpdateIntegration(updated);
                            }}
                            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <label htmlFor="handwritten" className="flex-1 cursor-pointer">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              ✍️ Assinatura Manuscrita
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                              Signatário desenha sua assinatura com dedo ou mouse.
                            </div>
                          </label>
                        </div>

                        {/* Alert de Alta Segurança */}
                        {(selectedIntegration.config_data?.facial_biometrics_enabled || selectedIntegration.config_data?.liveness_enabled) && (
                          <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                            <div className="text-sm font-medium text-green-800 dark:text-green-300">
                              ✓ Alta Segurança Ativada
                            </div>
                            <div className="text-xs text-green-700 dark:text-green-400 mt-1">
                              Todos os contratos enviados para o Clicksign exigirão validação biométrica.
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* OpenAI Configuration */}
                {selectedIntegration.integration_name === 'openai' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Modelo GPT
                      </label>
                      <select
                        value={selectedIntegration.config_data?.model || 'gpt-4-turbo-preview'}
                        onChange={(e) => {
                          const updated = {
                            ...selectedIntegration,
                            config_data: {
                              ...selectedIntegration.config_data,
                              model: e.target.value
                            }
                          };
                          setSelectedIntegration(updated);
                          handleUpdateIntegration(updated);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="gpt-4-turbo-preview">GPT-4 Turbo (Recomendado)</option>
                        <option value="gpt-4">GPT-4</option>
                        <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Mais rápido)</option>
                      </select>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        🤖 GPT-4 Turbo oferece melhor qualidade de resposta (~$0.02/conversa)
                        <br />
                        Obtenha sua API Key em: <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">platform.openai.com/api-keys</a>
                      </p>
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 p-4 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                        📊 Funcionalidades IA
                      </h4>
                      <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                        <li>✅ Atendimento automático 24/7 via WhatsApp</li>
                        <li>✅ Memória de contexto (últimas 10 mensagens)</li>
                        <li>✅ Detecção de intenção e sentimento</li>
                        <li>✅ Transferência inteligente para humano</li>
                        <li>✅ Comandos especiais (#menu, #ajuda, #humano)</li>
                        <li>✅ Extração automática de dados do cliente</li>
                      </ul>
                    </div>
                  </>
                )}

                {/* Anthropic Claude Configuration */}
                {selectedIntegration.integration_name === 'anthropic' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Modelo Claude
                      </label>
                      <select
                        value={selectedIntegration.config_data?.model || 'claude-3-5-sonnet-20241022'}
                        onChange={(e) => {
                          const updated = {
                            ...selectedIntegration,
                            config_data: {
                              ...selectedIntegration.config_data,
                              model: e.target.value
                            }
                          };
                          setSelectedIntegration(updated);
                          handleUpdateIntegration(updated);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet (Recomendado)</option>
                        <option value="claude-3-opus-20240229">Claude 3 Opus (Mais inteligente)</option>
                        <option value="claude-3-haiku-20240307">Claude 3 Haiku (Mais rápido)</option>
                      </select>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        🧠 Claude 3.5 Sonnet oferece excelente balanço qualidade/custo (~$0.01/conversa)
                        <br />
                        Obtenha sua API Key em: <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">console.anthropic.com</a>
                      </p>
                    </div>

                    <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 p-4 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                        🧠 Funcionalidades IA
                      </h4>
                      <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                        <li>✅ Atendimento automático 24/7 via WhatsApp</li>
                        <li>✅ Memória de contexto (últimas 10 mensagens)</li>
                        <li>✅ Detecção de intenção e sentimento</li>
                        <li>✅ Transferência inteligente para humano</li>
                        <li>✅ Comandos especiais (#menu, #ajuda, #humano)</li>
                        <li>✅ Extração automática de dados do cliente</li>
                      </ul>
                    </div>
                  </>
                )}

                {/* Asaas Configuration */}
                {selectedIntegration.integration_name === 'asaas' && (
                  <>
                    <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border border-green-200 dark:border-green-700 p-4 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                        💰 Asaas - Plataforma de Pagamentos Brasileira
                      </h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                        Integração completa com boletos bancários, PIX, cartão de crédito e assinaturas recorrentes.
                      </p>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div className="bg-white dark:bg-gray-800 p-2 rounded">
                          <div className="font-semibold text-gray-900 dark:text-white">Sandbox</div>
                          <div className="text-gray-600 dark:text-gray-400">Ambiente de testes</div>
                          <div className="text-blue-600 dark:text-blue-400 mt-1">sandbox.asaas.com</div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-2 rounded">
                          <div className="font-semibold text-gray-900 dark:text-white">Produção</div>
                          <div className="text-gray-600 dark:text-gray-400">Transações reais</div>
                          <div className="text-green-600 dark:text-green-400 mt-1">api.asaas.com</div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 p-4 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                        🔑 Como Obter sua API Key
                      </h4>
                      <ol className="text-xs text-gray-600 dark:text-gray-400 space-y-2">
                        <li>1️⃣ Acesse <a href="https://www.asaas.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline font-semibold">asaas.com</a> e crie sua conta</li>
                        <li>2️⃣ No painel, acesse <strong>Integrações → API Key</strong></li>
                        <li>3️⃣ Para testes, use a <strong>API Key Sandbox</strong> (prefixo $aact_hmlg_)</li>
                        <li>4️⃣ Para produção, use a <strong>API Key Produção</strong> (prefixo $aact_prod_)</li>
                        <li>5️⃣ Cole a chave no campo acima e ative o modo correspondente</li>
                      </ol>
                      <div className="mt-3 pt-3 border-t border-yellow-300 dark:border-yellow-700">
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          📚 <a href="https://docs.asaas.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">Documentação oficial da API</a>
                        </p>
                      </div>
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 p-4 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                        🔔 Configuração de Webhooks
                      </h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                        Para receber notificações automáticas de pagamentos, configure o webhook no painel do Asaas:
                      </p>
                      <div className="bg-white dark:bg-gray-800 p-3 rounded font-mono text-xs text-gray-900 dark:text-white break-all">
                        https://viploclocadora.com.br/api/asaas/webhooks
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                        <strong>Eventos suportados:</strong> PAYMENT_RECEIVED, PAYMENT_CONFIRMED, PAYMENT_OVERDUE
                      </p>
                    </div>

                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 p-4 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                        ✅ Funcionalidades Disponíveis
                      </h4>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex items-center gap-2">
                          <span className="text-green-600 dark:text-green-400">●</span>
                          <span className="text-gray-700 dark:text-gray-300">Boletos Bancários</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-green-600 dark:text-green-400">●</span>
                          <span className="text-gray-700 dark:text-gray-300">PIX QR Code</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-green-600 dark:text-green-400">●</span>
                          <span className="text-gray-700 dark:text-gray-300">Cartão de Crédito</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-green-600 dark:text-green-400">●</span>
                          <span className="text-gray-700 dark:text-gray-300">Assinaturas Recorrentes</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-green-600 dark:text-green-400">●</span>
                          <span className="text-gray-700 dark:text-gray-300">Pagamento de Contas</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-green-600 dark:text-green-400">●</span>
                          <span className="text-gray-700 dark:text-gray-300">Split de Pagamentos</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-3 pt-3 border-t border-green-300 dark:border-green-700">
                        💡 Habilite as funcionalidades desejadas na seção abaixo
                      </p>
                    </div>
                  </>
                )}

                {/* API Secret (se necessário) */}
                {selectedIntegration.integration_name !== 'getrak' && selectedIntegration.integration_name !== 'whatsapp' && selectedIntegration.integration_name !== 'clicksign' && selectedIntegration.integration_name !== 'openai' && selectedIntegration.integration_name !== 'anthropic' && selectedIntegration.integration_name !== 'asaas' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      API Secret (opcional)
                    </label>
                    <input
                      type="password"
                      value={selectedIntegration.api_secret || ''}
                      onChange={(e) => setSelectedIntegration({
                        ...selectedIntegration,
                        api_secret: e.target.value
                      })}
                      onBlur={() => handleUpdateIntegration(selectedIntegration)}
                      placeholder="Cole seu API Secret aqui"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                )}

                {/* Testar Conexão */}
                <button
                  onClick={handleTestConnection}
                  disabled={testing || !selectedIntegration.api_key}
                  className="w-full btn btn-secondary flex items-center justify-center space-x-2"
                >
                  <ArrowPathIcon className={`w-5 h-5 ${testing ? 'animate-spin' : ''}`} />
                  <span>{testing ? 'Testando...' : 'Testar Conexão'}</span>
                </button>
              </div>
            </div>

            {/* Funcionalidades */}
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Funcionalidades Disponíveis
              </h2>
              
              <div className="space-y-3">
                {features.map((feature) => (
                  <div
                    key={feature.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white capitalize">
                        {feature.feature_key.replace(/_/g, ' ')}
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={feature.enabled}
                        onChange={(e) => handleToggleFeature(feature.feature_key, e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default IntegrationsPage